import type { FlagValues } from "../components/LaunchDarkly/flags";
import { useFeatureFlags } from "../components/LaunchDarkly/useFeatureFlags";
import { PAGES } from "../pages/registry";
import { type PageDefinition, pageFlagKey } from "../pages/types";

/**
 * Whether a page is shown, and why.
 *
 * - `enabled`  LaunchDarkly serves the page's flag `true` for this user.
 * - `preview`  The flag is off, but the page is listed in
 *              `VITE_ML_FRONTEND_PREVIEW_PAGES` and this is the local dev server.
 * - `hidden`   Neither. The route renders not-found and nothing links to it.
 */
type PageAccess = "enabled" | "preview" | "hidden";

export function pageAccess(
  page: PageDefinition,
  flags: FlagValues,
): PageAccess {
  if (flags[pageFlagKey(page)]) {
    return "enabled";
  }

  return isLocalPreview(page.id) ? "preview" : "hidden";
}

/**
 * The pages the sidebar and landing page link to: the same rule each page's
 * route applies, so a link never leads to not-found.
 */
export function useVisiblePages(): readonly PageDefinition[] {
  const flags = useFeatureFlags();
  return PAGES.filter((page) => pageAccess(page, flags) !== "hidden");
}

/**
 * Local preview: lets a developer see a page they're building before its
 * LaunchDarkly flag exists.
 *
 * Only honoured under the Vite dev server. `import.meta.env.DEV` is replaced
 * with `false` in every `vite build`, so a deployed bundle never reads the
 * variable at all — the flag stays the only way to turn a page on outside a
 * developer's machine. The variable belongs in the git-ignored `.env.local`,
 * never in committed config.
 *
 * Page flags only. The app-level `mlFrontendEnabled` flag, the NikolaTeam role
 * check and the ML API's Cognito allowlist all still apply.
 */
function isLocalPreview(pageId: string): boolean {
  if (!import.meta.env.DEV) {
    return false;
  }

  const listed = (
    (import.meta.env.VITE_ML_FRONTEND_PREVIEW_PAGES as string | undefined) ?? ""
  )
    .split(",")
    .map((id) => id.trim());

  // `*` previews every page folder, including ones copied a moment ago.
  return listed.includes("*") || listed.includes(pageId);
}
